interface RetryOptions {
  maxRetries?: number;
  delayMs?: number;
}

export async function withRetry<T>(
  fn: () => Promise<T>,
  { maxRetries = 1, delayMs = 1000 }: RetryOptions = {},
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries) {
        // delay the next attempt
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
  }
  throw lastError;
}

/**
 * Runs async operations over items with a sliding window of concurrency.
 * Yields each result in completion order (so a slow item does not block others).
 *
 * Why sliding window instead of fixed batches (Promise.all per batch)?
 * With a fixed batch the entire next batch is blocked until the slowest item in the
 * current batch finishes. A sliding window keeps exactly `concurrency` items in flight at
 * all times: as soon as one completes the next item is started immediately. This prevents
 * a single large file from stalling a whole batch worth of smaller files.
 *
 * Use for streaming or when order does not matter (e.g. summing sizes).
 */
export async function* mapConcurrentGenerator<T, R>(
  items: T[],
  fn: (item: T) => Promise<R>,
  concurrency: number,
  signal?: AbortSignal,
): AsyncGenerator<R> {
  type Settled = { key: number; value: R } | { key: number; error: unknown };
  let nextIndex = 0;
  const inFlight = new Map<number, Promise<Settled>>();

  while (nextIndex < items.length || inFlight.size > 0) {
    if (signal?.aborted) throw new DOMException('Operation cancelled', 'AbortError');

    while (nextIndex < items.length && inFlight.size < concurrency) {
      const key = nextIndex;
      const item = items[nextIndex];
      nextIndex += 1;
      // Attach .catch so that when Promise.race propagates a rejection and we exit,
      // the remaining in-flight promises do not become unhandled rejections.
      const promise = fn(item)
        .then((value): Settled => ({ key, value }))
        .catch((error): Settled => ({ key, error }));
      inFlight.set(key, promise);
    }

    if (inFlight.size === 0) break;

    const settled = await Promise.race(inFlight.values());
    inFlight.delete(settled.key);
    if ('error' in settled) throw settled.error;
    yield settled.value;
  }
}
